/**
 * ССЫЛКА-ПРИГЛАШЕНИЕ В КОМНАТУ (Антон 16.10 10:33).
 *
 * Здесь только ЧТЕНИЕ состояния комнаты и вход (join) по явному нажатию
 * человека. Правила входа комнаты (m.room.join_rules) клиент НЕ меняет:
 * открыть закрытую комнату — изменение доступа, это решение Антона, а не
 * побочный эффект кнопки «Поделиться».
 *
 * ЗАМЕР НА ЖИВОМ СЕРВЕРЕ 16.10 (read-only, room_stats_state, комнаты с
 * хотя бы одним участником): 142 комнаты, ВСЕ join_rule = invite (140
 * обычных + 2 пространства), canonical alias есть у 1. Значит на сегодня
 * ссылка открывает комнату тем, кто уже в ней или приглашён; остальные
 * видят «Комната закрытая, попросите приглашение».
 *
 * Knock (MSC2403) намеренно НЕ отправляем: отдельное решение (постановка).
 */
import type { MatrixClient, Room } from "matrix-js-sdk";

import { isRoomAlias, serverNameOf } from "../ui/router";

/** Алиас для ссылки: canonical, иначе первый alt_alias, иначе null. */
export function roomLinkAlias(room: Room): string | null {
  const canonical = room.getCanonicalAlias();
  if (canonical) return canonical;
  const alt = room.getAltAliases();
  return alt.length > 0 ? alt[0] : null;
}

export type LinkJoinRule = "public" | "invite" | "knock" | "restricted" | "knock_restricted" | "private" | "unknown";

function normRule(v: unknown): LinkJoinRule {
  switch (v) {
    case "public":
    case "invite":
    case "knock":
    case "restricted":
    case "knock_restricted":
    case "private":
      return v;
    default:
      return "unknown";
  }
}

/** Правило входа комнаты, в которой мы состоим (из локального state). */
export function joinRuleOf(room: Room): LinkJoinRule {
  return normRule(room.getJoinRule());
}

/**
 * Что делать с правилом входа на экране «комната по ссылке».
 *  - join   — можно нажать «Войти в комнату» (public; restricted — сервер
 *             сам проверит членство в пространстве);
 *  - knock  — можно было бы попроситься, но knock не отправляем;
 *  - closed — только по приглашению (или правило неизвестно).
 */
export function gateActionFor(rule: LinkJoinRule): "join" | "knock" | "closed" {
  if (rule === "public" || rule === "restricted") return "join";
  if (rule === "knock" || rule === "knock_restricted") return "knock";
  return "closed";
}

export interface LinkPreview {
  /** Итоговый id комнаты (алиас уже разрешён), если сервер его сообщил. */
  roomId: string | null;
  name: string | null;
  topic: string | null;
  members: number | null;
  joinRule: LinkJoinRule;
  /** Сервер не нашёл ни алиас, ни комнату. */
  notFound: boolean;
}

/**
 * Предпросмотр комнаты, в которой мы НЕ состоим.
 *
 * 1. Алиас → room_id через каталог (GET /directory/room/<alias>): если
 *    алиаса нет — честное «не найдено».
 * 2. Room summary (MSC3266; SDK ходит в im.nheko.summary). Synapse отдаёт
 *    его только для комнат, доступных без приглашения (public / knock /
 *    restricted / world_readable). Для invite-only — ошибку. Поэтому
 *    ошибка summary = «закрытая»: ровно то, что видит сервер.
 */
export async function previewRoomFromLink(
  client: MatrixClient,
  idOrAlias: string,
  via: string[] | undefined,
): Promise<LinkPreview> {
  let roomId: string | null = isRoomAlias(idOrAlias) ? null : idOrAlias;
  let servers = via && via.length > 0 ? via : [];
  if (isRoomAlias(idOrAlias)) {
    try {
      const res = await client.getRoomIdForAlias(idOrAlias);
      roomId = res.room_id;
      if (servers.length === 0 && res.servers?.length) servers = res.servers;
    } catch {
      return { roomId: null, name: null, topic: null, members: null, joinRule: "unknown", notFound: true };
    }
  }
  if (servers.length === 0) {
    const s = serverNameOf(idOrAlias);
    if (s) servers = [s];
  }
  try {
    const sum = await client.getRoomSummary(roomId ?? idOrAlias, servers);
    return {
      roomId: sum.room_id ?? roomId,
      name: sum.name ?? (sum as { canonical_alias?: string }).canonical_alias ?? null,
      topic: sum.topic ?? null,
      members: typeof sum.num_joined_members === "number" ? sum.num_joined_members : null,
      joinRule: normRule((sum as { join_rule?: unknown }).join_rule),
      notFound: false,
    };
  } catch {
    return { roomId, name: null, topic: null, members: null, joinRule: "invite", notFound: false };
  }
}

/** Вход по ссылке — только по нажатию «Войти в комнату». */
export async function joinFromLink(
  client: MatrixClient,
  idOrAlias: string,
  via: string[] | undefined,
): Promise<string> {
  const servers = via && via.length > 0 ? via : (serverNameOf(idOrAlias) ? [serverNameOf(idOrAlias) as string] : []);
  const room = await client.joinRoom(idOrAlias, servers.length > 0 ? { viaServers: servers } : undefined);
  return room.roomId;
}
